import moment, { Moment } from 'moment';
import noop from 'utils/noop';
import styled from 'styled-components';
import {
  BasicSelectField,
  DatePickerField,
  InputField,
  RadioListField,
  TimePickerField,
} from 'lib/iterable-hook-form/FormField';
import { OptionText } from 'modules/campaigns/editor/styles';
import { EndEligibilityType, ScheduleEligibilityFormState } from 'modules/campaigns/editor/types';
import { useEffect } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { match } from 'ts-pattern';
import { ErrorText, FormField } from 'ui/formField';
import { Help } from 'ui/icons';
import { Tooltip } from 'ui/tooltip';
import { RadioListVariantProps } from 'ui/radioList';
import { colors, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { InputRow } from './styles';

export const EndEligibilityTypeLabel = styled(OptionText)`
  width: ${pxToRem(46)};
`;

export const EndSendsDatePickerField = styled(DatePickerField)`
  form {
    width: ${pxToRem(145)};
  }
`;

export const EndEligibilityTimePickerField = styled(TimePickerField)`
  margin-left: ${spacing.base};
  width: ${pxToRem(145)};
`;

export const EndEligibilityNumberInputField = styled(InputField)`
  width: ${pxToRem(145)};

  input {
    width: 0;
  }
`;

export const EndEligibilityTimeUnitField = styled(BasicSelectField)`
  width: ${pxToRem(145)};

  input {
    width: 0;
  }
`;

export const EndSendsRadioListField = styled(RadioListField)`
  width: 100%;
`;

const StyledErrorText = styled(ErrorText)`
  margin: 0 0 0 ${spacing.double};
`;

const StyledHelpIcon = styled(Help)`
  margin-left: ${spacing.eighth};
`;

export const END_ELIGIBILITY_SCHEDULE_TEST_ID = 'end-eligibility-schedule-fields';

const EndEligibilityOnDate = () => {
  const { errors } = useFormState<ScheduleEligibilityFormState>({
    name: ['endDate', 'endTime'],
  });
  const startDate: Moment = useWatch({
    name: 'startDate',
  });

  const endDateError = errors?.endDate;
  const minEndDate = moment(startDate).add(1, 'day');

  return (
    <>
      <InputRow>
        <EndEligibilityTypeLabel>On</EndEligibilityTypeLabel>
        <EndSendsDatePickerField
          name="endDate"
          validDateRange={{ start: minEndDate }}
          automaticallyAvoidCollisions
        />
        <EndEligibilityTimePickerField name="endTime" />
      </InputRow>
      {endDateError?.message && <StyledErrorText>{endDateError.message}</StyledErrorText>}
    </>
  );
};

// Just removing UI access to end after scheduling since this will be added back after support added
// const EndRecurrenceAfter = () => {
//   const { errors } = useFormState<ScheduleEligibilityFormState>({
//     name: ['endAfterValue'],
//   });
//   const {
//     field: { value },
//   } = useController({
//     name: 'endAfterValue',
//   });

//   const timeUnitOptions = Object.values(TimeUnitType).map((unit) => ({
//     id: unit,
//     label: value === '1' ? unit.slice(0, -1) : unit,
//     value: unit,
//   }));

//   const endAfterValueError = errors?.endAfterValue;

//   return (
//     <>
//       <InputRow>
//         <EndEligibilityTypeLabel>After</EndEligibilityTypeLabel>
//         <EndEligibilityNumberInputField
//           name="endAfterValue"
//           type="number"
//           placeholder="0"
//           onKeyDown={(e: Event) => e.stopPropagation()}
//         />
//         <EndEligibilityTimeUnitField
//           name="endAfterUnit"
//           options={timeUnitOptions}
//           defaultValue={TimeUnitType.Days}
//         />
//       </InputRow>
//       {endAfterValueError?.message && (
//         <StyledErrorText>{endAfterValueError.message}</StyledErrorText>
//       )}
//     </>
//   );
// };

const END_ELIGIBILITY_OPTIONS: RadioListVariantProps['options'] = [
  {
    id: EndEligibilityType.Never,
    value: EndEligibilityType.Never,
    name: () => <OptionText>Never</OptionText>,
  },
  {
    id: EndEligibilityType.OnDate,
    value: EndEligibilityType.OnDate,
    name: EndEligibilityOnDate,
  },
  // {
  //   id: EndEligibilityType.After,
  //   value: EndEligibilityType.After,
  //   name: EndRecurrenceAfter,
  // },
];

export const EndEligibilitySchedule = () => {
  const { watch, trigger } = useFormContext();

  useEffect(() => {
    watch((_, { name }) => {
      match(name)
        .with('endType', () => {
          // trigger('endAfterValue');
          trigger('endDate');
        })
        .with('startDate', () => trigger('endDate'))
        .with('endDate', () => trigger('endDate'))
        .otherwise(noop);
    });
  }, [watch, trigger]);

  const formFieldLabel = (
    <>
      End for users
      <Tooltip tooltipContent="A message will also stop displaying if a user no longer matches eligibility.">
        <StyledHelpIcon color={colors.textSecondary} height={20} width={20} />
      </Tooltip>
    </>
  );

  return (
    <FormField data-test={END_ELIGIBILITY_SCHEDULE_TEST_ID} labelText={formFieldLabel}>
      <EndSendsRadioListField name="endType" options={END_ELIGIBILITY_OPTIONS} />
    </FormField>
  );
};
